import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supportService, Ticket } from '../services/supportService';
import { Navigation } from './Navigation';
import './ContactSupport.css';

type Tab = 'submit' | 'tickets';

const CATEGORIES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'feature-request', label: 'Feature Request' },
];

export const ContactSupport: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('submit');

  // Submit form state
  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Tickets state
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketsError, setTicketsError] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'tickets' && token) {
      loadTickets();
    }
  }, [activeTab, token]);

  const loadTickets = async () => {
    if (!token) return;
    setLoadingTickets(true);
    setTicketsError(null);
    try {
      const data = await supportService.getTickets(token);
      setTickets(data.tickets || []);
    } catch (err: any) {
      setTicketsError(err.response?.data?.error || 'Failed to load tickets');
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setSubmitting(true);
    setSubmitMessage(null);
    setSubmitError(null);

    try {
      const data = await supportService.submitTicket(subject, description, category, token);
      setSubmitMessage(`Ticket created successfully! Case number: ${data.ticket.caseNumber}`);
      setSubject('');
      setDescription('');
      setCategory('general');
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'open': return 'badge-open';
      case 'in-progress': return 'badge-in-progress';
      case 'resolved': return 'badge-resolved';
      case 'closed': return 'badge-closed';
      default: return '';
    }
  };

  return (
    <div className="contact-support-container">
      <Navigation />

      <div className="contact-support-page">
          <div className="contact-support-header">
            <h1>Contact Support</h1>
            <p>Submit a ticket or check the status of your existing requests.</p>
          </div>

          <div className="contact-support-tabs">
            <button
              className={`tab-btn ${activeTab === 'submit' ? 'active' : ''}`}
              onClick={() => setActiveTab('submit')}
            >
              Submit Ticket
            </button>
            <button
              className={`tab-btn ${activeTab === 'tickets' ? 'active' : ''}`}
              onClick={() => setActiveTab('tickets')}
            >
              My Tickets
            </button>
          </div>

          {activeTab === 'submit' && (
            <div className="contact-support-form-card">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    id="subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief summary of your issue"
                    minLength={5}
                    maxLength={200}
                    required
                  />
                  <span className="char-count">{subject.length}/200</span>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your issue in detail..."
                    minLength={10}
                    maxLength={5000}
                    rows={8}
                    required
                  />
                  <span className="char-count">{description.length}/5000</span>
                </div>

                {submitError && <div className="support-alert support-alert-error">{submitError}</div>}
                {submitMessage && <div className="support-alert support-alert-success">{submitMessage}</div>}

                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'tickets' && (
            <div className="contact-support-tickets">
              {loadingTickets && (
                <div className="tickets-loading">
                  <div className="spinner"></div>
                  <p>Loading tickets...</p>
                </div>
              )}

              {ticketsError && (
                <div className="support-alert support-alert-error">{ticketsError}</div>
              )}

              {!loadingTickets && !ticketsError && tickets.length === 0 && (
                <div className="tickets-empty">
                  <p>You haven't submitted any support tickets yet.</p>
                </div>
              )}

              {tickets.map((ticket) => (
                <div key={ticket.caseNumber} className="ticket-card">
                  <div className="ticket-card-header">
                    <span className="ticket-case">{ticket.caseNumber}</span>
                    <span className={`ticket-status ${statusBadgeClass(ticket.status)}`}>
                      {ticket.status}
                    </span>
                  </div>
                  <h3 className="ticket-subject">{ticket.subject}</h3>
                  <div className="ticket-meta">
                    <span className="ticket-category">{ticket.category}</span>
                    <span className="ticket-date">
                      {new Date(ticket.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};
